
import { isBrowser, isChrome, isEdge, isFirefox, isSafari, isWindows, isMac, getChromeVersion, isIOSDevice, getIOSVersion } from '../../compat.js';

// Chrome version required for WebGPU compatibility mode. Currently set to Infinity for production. In Chrome, version
// 139 introduced compatibility mode as experimental, requiring chrome://flags/#enable-unsafe-webgpu to be enabled.
// @todo: consider making WebGPU-compatibility mode availability a feature flag instead of a hardcoded version check.
const CHROME_WEBGPU_COMPATIBILITY_VERSION = Infinity; // disabled for now
// const CHROME_WEBGPU_COMPATIBILITY_VERSION = 139; // uncomment for testing

// Caches the asynchronous check for WebGPU support for synchronous access
let _isWebGPUSupportedCache;

/**
 * Requests a WebGPU adapter with the given options. Handles exceptions gracefully - in some cases, the WebGPU API is
 * available but no adapter is present, as in https://autodesk.atlassian.net/browse/VIZX-3420.
 *
 * @param {GPURequestAdapterOptions} [adapterOptions={}] - Options to pass to navigator.gpu.requestAdapter.
 * @returns {Promise<GPUAdapter|null>} Adapter if available, otherwise null.
 */
export async function requestWebGPUAdapter(adapterOptions = {}) {
  if (!isBrowser || !navigator.gpu) return null;
  try {
    const adapter = await navigator.gpu.requestAdapter(adapterOptions);
    return adapter;
  } catch {
    return null;
  }
}

/**
 * Validates features and limits against requirements. Works with GPUAdapter, GPUDevice, or any object with
 * `features` (Set-like) and `limits` (object with numeric properties).
 *
 * @param {{ features: GPUSupportedFeatures, limits: GPUSupportedLimits }} target - Adapter, Device, or compatible mock
 * @param {Set<string>|string[]} requiredFeatures - Features that must be present (validation fails if not met)
 * @param {Object} [preferredLimits={}] - Limits we prefer (triggers degraded if not met, but validation still passes)
 * @param {Object} [requiredLimits={}] - Limits that must be met (validation fails if not met)
 * @returns {{ valid: boolean, degraded: Object }} `valid` is true if all required features and limits are met.
 *   `degraded` contains limits that are below preferred but meet required (for overriding in device descriptor).
 */
export function validateWebGPUFeaturesAndLimits(target, requiredFeatures, preferredLimits = {}, requiredLimits = {}) {
  const { features, limits } = target;
  const degraded = {};

  // Validate required features
  const unsupportedFeatures = new Set();
  for (const feature of requiredFeatures) {
    if (!features.has(feature)) {
      unsupportedFeatures.add(feature);
    }
  }

  // Validate required limits
  const unsupportedLimits = new Set();
  for (const limit in requiredLimits) {
    console.assert(limits[limit] !== undefined, `Limit ${limit} is undefined.`);
    if (limits[limit] < requiredLimits[limit]) {
      unsupportedLimits.add(limit);
    }
  }

  // Validate preferred limits (only if not already failing required)
  const degradedLimits = new Set();
  for (const limit in preferredLimits) {
    console.assert(limits[limit] !== undefined, `Limit ${limit} is undefined.`);
    if (unsupportedLimits.has(limit)) continue; // already failed required

    if (limits[limit] < preferredLimits[limit]) {
      degradedLimits.add(limit);
      degraded[limit] = limits[limit];
    }
  }

  // Log unsupported features and limits
  if (unsupportedFeatures.size > 0) {
    console.groupCollapsed(`WebGPU: missing required features (${unsupportedFeatures.size})`);
    for (const feature of unsupportedFeatures) {
      console.log(`- ${feature}`);
    }
    console.groupEnd();
  }

  if (unsupportedLimits.size > 0 || degradedLimits.size > 0) {
    console.groupCollapsed(`WebGPU: limits not met (${unsupportedLimits.size} required, ${degradedLimits.size} preferred)`);
    for (const limit of unsupportedLimits) {
      console.log(`- ${limit}: required >= ${requiredLimits[limit]}, available = ${limits[limit]}`);
    }
    for (const limit of degradedLimits) {
      console.log(`- ${limit}: preferred >= ${preferredLimits[limit]}, available = ${limits[limit]} (degraded)`);
    }
    console.groupEnd();
  }

  const valid = unsupportedFeatures.size === 0 && unsupportedLimits.size === 0;
  return { valid, degraded };
}

/**
 * A rendering-agnostic check on the general availability of the WebGPU API in the current environment.
 *
 * @param {GPURequestAdapterOptions} [adapterOptions={}] - Options to pass to navigator.gpu.requestAdapter.
 * @returns {Promise<boolean>} True if the adapter is available, otherwise false.
 */
export async function isWebGPUAvailable(adapterOptions = {}) {
  const adapter = await requestWebGPUAdapter(adapterOptions);
  return Boolean(adapter);
}

/**
 * A rendering-agnostic check if WebGPU is both (1) available and (2) supposed to be available on this client, based on
 * OS, browser, GPU, and build-type. The first call caches the result which is returned for subsequent calls. After the
 * first call, the cached value can be accessed synchronously with isWebGPUSupported_cached().
 *
 * @returns {Promise<boolean>} True if the client supports WebGPU, otherwise false.
 */
export async function isWebGPUSupported() {
  if (_isWebGPUSupportedCache !== undefined) return _isWebGPUSupportedCache;

  const isAdapterAvailable = await isWebGPUAvailable();
  if (!isAdapterAvailable) {
    _isWebGPUSupportedCache = false;
    return _isWebGPUSupportedCache;
  }

  const isChromium = !isIOSDevice() && (isChrome() || isEdge()); // Chromium based browsers are supported
  const isSupportedWindows = isWindows() && isFirefox();
  const isSupportedMac = isMac() && (isFirefox() || isSafari());
  const isSupportedIOS = isIOSDevice() && parseInt(getIOSVersion(), 10) >= 18; // WebGPU is supported and enabled by default in iOS 26 or later

  // Developer sandbox bypasses the browser allow-list to allow testing on Safari and other browsers.
  const isDeveloperSandbox = isBrowser && document.URL.match(/localhost:[0-9]{1,5}\/(?:index\.html)?(?:\?|$)/) !== null;

  _isWebGPUSupportedCache = isSupportedWindows || isSupportedMac || isSupportedIOS || isChromium || isDeveloperSandbox;
  return _isWebGPUSupportedCache;
}

/**
 * Synchronous check for WebGPU support. Must call isWebGPUSupported() first.
 * @returns {boolean} True if the client supports WebGPU, otherwise false.
 */
export function isWebGPUSupported_cached() {
  if (_isWebGPUSupportedCache === undefined) {
    console.warn("isWebGPUSupported_cached() called before isWebGPUSupported(). This will always return false.");
    return false;
  }
  return _isWebGPUSupportedCache;
}

/**
 * Returns true if we support a compatibility mode on this browser (experimental), expect false for production.
 *
 * > [The] WebGPU compatibility mode offers a solution by providing an opt-in, slightly restricted version of the
 * > WebGPU API. This mode is designed to run older graphics APIs like OpenGL ES 3.1 and Direct3D11, significantly
 * > expanding your application's reach to devices that don't support modern, explicit graphics APIs required by core
 * > WebGPU.
 *
 * @see https://developer.chrome.com/blog/new-in-webgpu-139#origin_trial_for_webgpu_compatibility_mode
 * @returns {boolean} True if WebGPU-compatibility mode is expected to be supported, otherwise false.
 */
export function expectWebGPUCompatibilitySupport() {
  const chromeVersion = getChromeVersion();
  return chromeVersion && chromeVersion >= CHROME_WEBGPU_COMPATIBILITY_VERSION;
}